import { useState } from "react";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import { PageHeader, Card } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Webhook, Send } from "lucide-react";

export default function PostbackTester() {
  const [form, setForm] = useState({ click_id: "", payout: "25.00", tx_id: "", status: "approved" });
  const [logs, setLogs] = useState([]);
  const [firing, setFiring] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const url = `${BACKEND_URL}/api/postback?click_id=${form.click_id || "{click_id}"}&payout=${form.payout}&tx_id=${form.tx_id || "auto"}&status=${form.status}`;

  const fire = async () => {
    setFiring(true);
    const started = Date.now();
    try {
      const res = await axios.get(`${BACKEND_URL}/api/postback`, {
        params: { click_id: form.click_id, payout: form.payout, tx_id: form.tx_id, status: form.status },
      });
      const entry = { time: new Date().toLocaleTimeString(), ms: Date.now() - started, status: 200, body: res.data };
      setLogs((l) => [entry, ...l]);
      if (res.data.success) toast.success(`Conversion recorded — ${res.data.conversion_id}`);
      else toast.error(res.data.error || "Postback rejected");
    } catch (e) {
      setLogs((l) => [{ time: new Date().toLocaleTimeString(), ms: Date.now() - started, status: e.response?.status || 0, body: e.response?.data || { error: e.message } }, ...l]);
      toast.error("Request failed");
    } finally {
      setFiring(false);
    }
  };

  return (
    <div data-testid="postback-tester-page">
      <PageHeader title="Postback Tester" subtitle="Simulate an S2S conversion postback from a network and debug attribution" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 fade-up">
        <Card className="p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Webhook className="h-4 w-4 text-indigo-500" /> Simulate Postback
          </h3>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Click ID (from Clicks Log)</Label>
              <Input data-testid="postback-clickid-input" value={form.click_id} onChange={(e) => set("click_id", e.target.value)} placeholder="clk_xxxxxxxxxxxxxx" className="mt-1 font-mono text-xs" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Payout ($)</Label>
                <Input data-testid="postback-payout-input" type="number" step="0.01" value={form.payout} onChange={(e) => set("payout", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Transaction ID</Label>
                <Input data-testid="postback-txid-input" value={form.tx_id} onChange={(e) => set("tx_id", e.target.value)} placeholder="auto" className="mt-1 font-mono text-xs" />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["approved", "pending", "rejected"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="bg-slate-900 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">GET Request</div>
              <code className="text-[11px] text-emerald-400 break-all">{url}</code>
            </div>
            <Button data-testid="fire-postback-button" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={!form.click_id || firing} onClick={fire}>
              <Send className="h-4 w-4 mr-2" /> {firing ? "Firing…" : "Fire Test Postback"}
            </Button>
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Response Console</h3>
          <div className="space-y-3 max-h-[420px] overflow-y-auto scrollbar-thin" data-testid="postback-console">
            {logs.length === 0 && <div className="text-xs text-slate-400">No requests fired yet. Grab a click ID from the Clicks Log and test attribution.</div>}
            {logs.map((l, i) => (
              <div key={i} className="bg-slate-900 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${l.status === 200 && l.body.success ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                    {l.status} {l.body.success ? "OK" : "ERR"}
                  </span>
                  <span className="text-[10px] text-slate-500">{l.time} · {l.ms}ms</span>
                </div>
                <pre className="text-[11px] text-sky-300 whitespace-pre-wrap break-all">{JSON.stringify(l.body, null, 2)}</pre>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
